import { UserIcon } from "@minervaproject/fds";

import { ExpandableTableRow } from "./expandable_table_row";

import { PreclassCheckMessages } from "components/local_modal/preclass_check/preclass_check_messages";
import { useArraySelector } from "hooks/redux_hooks";
import { useUserProperties } from "hooks/use_user_properties";
import { MonitoringEventType, MonitoringEventTypeType } from "shared/types/monitoring_event";
import { PreclassTestOutcomeType } from "shared/types/preclass_check";
import { selectMonitoringEventsByUserIdAndTypes } from "store/selectors/monitoring";
import { formatDateTime } from "utils/date";

const MAX_PAGE_LOADS = 1;
const PRECLASS_CHECK_MESSAGES = new PreclassCheckMessages();
const USER_FRIENDLY_DETAILS_MAP: Record<string, string> = {
  "preclass-check-success": "Success",
  "preclass-check-fail": "Pre-class Check Failure",
  "clicked-exit": "Clicked Exit",
  "unknown-navigation": "Unknown Navigation",
};

function TableRow({
  event,
  details,
  expandedDetails,
}: {
  event: MonitoringEventType;
  details: string | undefined;
  expandedDetails?: JSX.Element;
}): JSX.Element {
  return expandedDetails ? (
    <ExpandableTableRow key={event.id} expandedContent={expandedDetails} colSpan={3} error>
      <td className="col-3 text-red">{event.type}</td>
      <td className="col-4 text-red">{details}</td>
      <td className="col-4 text-red">{formatDateTime(event.timestamp, true)}</td>
    </ExpandableTableRow>
  ) : (
    <tr key={event.id}>
      {/* eslint-disable-next-line jsx-a11y/control-has-associated-label */}
      <td className="col-1"> </td>
      <td className="col-3">{event.type}</td>
      <td className="col-4 truncate" title={details}>
        {details}
      </td>
      <td className="col-4">{formatDateTime(event.timestamp, true)}</td>
    </tr>
  );
}

function UserStatus({ userId }: { userId: string }) {
  const user = useUserProperties(userId, ["is_present"]);
  const events = useArraySelector(
    selectMonitoringEventsByUserIdAndTypes(userId, [
      MonitoringEventTypeType.ENTER_CLASS,
      MonitoringEventTypeType.LEAVE_CLASS,
      MonitoringEventTypeType.PRECLASS_CHECK_FAIL,
      MonitoringEventTypeType.PRECLASS_CHECK_SUCCESS,
    ]),
    ["id"],
  );
  const enterClassEvents = events.filter(
    (event) => event.type === MonitoringEventTypeType.ENTER_CLASS,
  );

  let precedingEnterEvent: MonitoringEventType | null = null;
  const tableRows: JSX.Element[] = [];
  events.forEach((event) => {
    let details;
    let expandedDetails: JSX.Element;
    if (event.type === MonitoringEventTypeType.ENTER_CLASS) {
      if (precedingEnterEvent) {
        tableRows.push(
          <TableRow key={precedingEnterEvent.id} event={precedingEnterEvent} details="" />,
        );
      }
      precedingEnterEvent = event;
      return;
    }
    if (event.type === MonitoringEventTypeType.LEAVE_CLASS) {
      if (precedingEnterEvent) {
        tableRows.push(
          <TableRow key={precedingEnterEvent.id} event={precedingEnterEvent} details="" />,
        );
      }
      details = USER_FRIENDLY_DETAILS_MAP[event.contextData.reason] || event.contextData.reason;
      tableRows.push(<TableRow key={event.id} event={event} details={details} />);
      precedingEnterEvent = null;
      return;
    }
    if (precedingEnterEvent && event.type === MonitoringEventTypeType.PRECLASS_CHECK_SUCCESS) {
      details = USER_FRIENDLY_DETAILS_MAP[event.type];
      tableRows.push(
        <TableRow key={precedingEnterEvent.id} event={precedingEnterEvent} details={details} />,
      );
      precedingEnterEvent = null;
      return;
    }
    if (precedingEnterEvent && event.type === MonitoringEventTypeType.PRECLASS_CHECK_FAIL) {
      details = USER_FRIENDLY_DETAILS_MAP[event.type];
      const preClassTests = Object.values(event.contextData).filter(({ outcome }) => !!outcome);
      const failedPreclassTests = Object.values(event.contextData).filter(({ outcome }) =>
        [
          PreclassTestOutcomeType.FAIL,
          PreclassTestOutcomeType.EXCEPTION,
          PreclassTestOutcomeType.ABORT,
        ].includes(outcome),
      );
      const errors = failedPreclassTests.map((test) => test.error).join(", ");
      const message =
        PRECLASS_CHECK_MESSAGES.getHighestPriorityMessageFromResults(
          preClassTests.reduce((obj, val) => ({ ...obj, [val.name]: val }), {}),
        )?.message || "";
      expandedDetails = (
        <div>
          <div className="bold">Errors:</div>
          <div className="mb2">{errors}</div>
          <div className="bold">Message to User:</div>
          <div className="mb2">{message}</div>
        </div>
      );
      tableRows.push(
        <TableRow
          key={precedingEnterEvent.id}
          event={precedingEnterEvent}
          details={details}
          expandedDetails={expandedDetails}
        />,
      );
      precedingEnterEvent = null;
    }
  });

  if (!user) return null;

  return (
    <div className="mb10" data-testid="user-status">
      <h2 className="h2 mxn6">
        <UserIcon width={16} height={16} />
        <span className="ml2">User Status</span>
      </h2>
      <div className="flex items-center my3 body-s">
        <div className="mr3">
          Currently in Class: {user.is_present ? <span className="text-green">Yes</span> : "No"}
        </div>
        <div
          className={
            enterClassEvents.length > MAX_PAGE_LOADS ? "bg-red-tint-90 text-red px2 py1" : ""
          }
          style={{ borderRadius: "4px" }}
        >
          Page Loads: {enterClassEvents.length}
        </div>
      </div>
      {tableRows.length ? (
        <table className="fds-table">
          <thead>
            <tr>
              {/* eslint-disable-next-line jsx-a11y/control-has-associated-label */}
              <th className="col-1" />
              <th className="col-3">Event</th>
              <th className="col-4">Details</th>
              <th className="col-4">Time</th>
            </tr>
          </thead>
          <tbody>{tableRows}</tbody>
        </table>
      ) : (
        <div className="my3 body-s">The user has not entered class yet.</div>
      )}
    </div>
  );
}

export { TableRow, UserStatus };
